import React, { useEffect, useMemo } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { parse } from '../../util/urlHelpers';
import { buildCategorySlugMap, findCategoryBySlug } from '../../util/categoryHelpers';
import { constructQueryParamName } from '../../util/search';
import { getCategoryMetaTagsNormalized } from '../../util/categoryMetaTags';

import SearchPageWithGrid from '../SearchPage/SearchPageWithGrid';
import SearchPageWithMap from '../SearchPage/SearchPageWithMap';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

/**
 * CategoryPage - Wrapper for SearchPage with category-based routing
 *
 * This component:
 * 1. Extracts category slugs from URL params (/category/sports/golf)
 * 2. Maps slugs to category IDs using config
 * 3. Adds category search params to location
 * 4. Renders SearchPage with those params
 * 5. Uses history.replace to maintain clean URLs
 */
export const CategoryPageComponent = (props) => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const location = useLocation();
  const params = useParams();

  const { category: categorySlug, subcategory: subcategorySlug } = params;

  // Build slug map from category configuration
  const categories = config?.categoryConfiguration?.categories || [];
  const slugMap = useMemo(() => buildCategorySlugMap(categories), [categories]);

  // Find category data by slug
  const categoryData = findCategoryBySlug(slugMap, categorySlug, subcategorySlug);

  // If invalid category, show 404
  if (!categoryData) {
    return <NotFoundPage />;
  }

  // Get category filter configuration
  const categoryFilter = config?.search?.defaultFilters?.find(f => f.schemaType === 'category');

  if (!categoryFilter) {
    console.error('Category filter not configured in search.defaultFilters');
    return <NotFoundPage />;
  }

  const { key, scope } = categoryFilter;

  // Build search params from category data
  const categorySearchParams = {};

  if (categoryData.categoryId) {
    const param1 = constructQueryParamName(`${key}1`, scope);
    categorySearchParams[param1] = categoryData.categoryId;
  }

  if (categoryData.subcategoryId) {
    const param2 = constructQueryParamName(`${key}2`, scope);
    categorySearchParams[param2] = categoryData.subcategoryId;
  }

  // Merge with existing search params from URL
  const existingParams = parse(location.search);
  const mergedParams = { ...existingParams, ...categorySearchParams };

  // Create modified location with category params
  const modifiedLocation = {
    ...location,
    search: `?${new URLSearchParams(mergedParams).toString()}`,
    state: {
      ...location.state,
      categoryParams: categorySearchParams,
      fromCategoryPage: true,
    }
  };

  // Use history.replace on mount to ensure params are in history state
  useEffect(() => {
    // Check if we need to update the state
    const hasStateParams = location.state?.categoryParams;
    const stateParamsMatch = hasStateParams &&
      Object.keys(categorySearchParams).every(
        key => location.state.categoryParams[key] === categorySearchParams[key]
      );

    if (!stateParamsMatch) {
      // Replace current history entry with category params in state
      history.replace({
        pathname: location.pathname,
        search: location.search,
        state: {
          ...location.state,
          categoryParams: categorySearchParams,
          fromCategoryPage: true,
        }
      });
    }
  }, [categorySlug, subcategorySlug, history, location.pathname, location.search]);

  // Look up meta tags for this category/subcategory
  const categoryMetaTags = useMemo(() => {
    const categoryName = categoryData.categoryName;
    const subcategoryName = categoryData.subcategoryName || '';
    // Pass slugs for better matching (handles cases like "Books & Media" vs "Collectable Books")
    return getCategoryMetaTagsNormalized(categoryName, subcategoryName, categorySlug, subcategorySlug);
  }, [categoryData, categorySlug, subcategorySlug]);

  // Determine which SearchPage variant to use
  const SearchPage = config?.layout?.searchPage?.variantType === 'map'
    ? SearchPageWithMap
    : SearchPageWithGrid;

  // Pass modified location to SearchPage
  // SearchPage will read category params from the modified location.search
  return (
    <SearchPage
      {...props}
      location={modifiedLocation}
      params={{ ...props.params, categorySlug, subcategorySlug }}
      categoryMetaTitle={categoryMetaTags?.title}
      categoryMetaDescription={categoryMetaTags?.description}
    />
  );
};

// Connect to Redux (passing through)
const mapStateToProps = state => ({});

const CategoryPage = compose(
  connect(mapStateToProps)
)(CategoryPageComponent);

export default CategoryPage;
